import { defineStore } from 'pinia'
import { objectify } from 'radash'

import { Product } from '@/types/products.interfaces'
import {
  ProductError,
  ProductOrderState,
  ProductState,
  ProductMboCategory,
  CategoriesLoadingState,
} from '@/types/products.mbo.interfaces'
import { PurchasedProductsState } from '@/composables/usePurchasedProducts'

interface State {
  all: Record<string, Product>
  categories: Record<string, ProductMboCategory>
  disableActions: boolean
  categoriesLoadingState: CategoriesLoadingState
  purchasedProductsState: PurchasedProductsState
}

const useProductsStore = defineStore({
  id: 'products',
  state: (): State => ({
    all: {},
    disableActions: false,
    categories: {},
    purchasedProductsState: PurchasedProductsState.unstarted,
    categoriesLoadingState: CategoriesLoadingState.unstarted,
  }),

  actions: {
    setAllCategories(categories: Record<string, ProductMboCategory>) {
      this.categories = categories
    },

    setInBatch(products: Product[]) {
      const newBasicInfos = objectify(
        products.map((product) => {
          return this._getProperties(
            product.technical_info.technical_name,
            product,
          )
        }),
        (product) => product.technical_info.technical_name,
      )

      this.all = {
        ...this.all,
        ...newBasicInfos,
      }

      return newBasicInfos
    },

    setAll(data: Product[]) {
      this.all = objectify(data, (f) => f.technical_info.technical_name)
    },

    setOne(product: Product) {
      const technical_name =
        product.technical_info?.technical_name || (product.name as string)

      this.all[technical_name] = this._getProperties(technical_name, product)
    },

    _getProperties(index: string, infos: Partial<Product>): Product {
      const product = this.all[index]

      // last_release to detect upgrade only on shop 1.7
      // Can't be used from search because it's not filtered by shop version
      // If already on store (example for ps_mbo), don't overwrite it
      if (infos.technical_info && infos.technical_info.last_release === null)
        infos.technical_info.last_release =
          product?.technical_info?.last_release

      const properties = {
        configUrl: undefined,
        installed: false,
        installedVersion: undefined,
        orderState: ProductOrderState.unstarted,
        loading: undefined,
        purchased: false,
        ...product,
        ...infos,
      }

      properties.installed =
        properties.status !== undefined && properties.status !== 'uninstalled'

      return properties
    },

    setError(moduleName: string, error?: ProductError) {
      this.all[moduleName].error = error
    },

    setStateByName(name: string, state: Partial<ProductState>) {
      const existingModule = this.all[name]

      this.all[name] = this._getProperties(name, {
        ...existingModule,
        ...state,
      })
    },

    setPurchasedProductsState(state: PurchasedProductsState) {
      this.purchasedProductsState = state
    },

    setStateInBatch(states: ProductState[]) {
      const newStates = objectify(
        states.map((productState) =>
          this._getProperties(productState.name, productState),
        ),
        (product) =>
          product.technical_info?.technical_name || product.name || '',
      )

      this.all = {
        ...this.all,
        ...newStates,
      }
    },

    setCategoriesLoadingState(state: CategoriesLoadingState) {
      this.categoriesLoadingState = state
    },
  },

  getters: {
    getTrackingPropertiesByName(state: State) {
      const productsStore: Record<string, Product> = state.all
      return (name: string) => {
        const product: Product = productsStore[name]
        return {
          module_id: product.product.id_product,
          product_id: product.product.id_product,
          module_installed: product.installed,
          module_name: product.technical_info.technical_name,
          module_purchased: product.purchased || false,
          module_category_parent_name: product.categoryParent?.name,
          module_category_parent_english_name:
            product.categoryParent?.english_name,
          module_category_parent_id: product.categoryParent?.id_category,
          module_display_name: product.product_lang.product_name,
          module_category: product.category?.id_category,
          module_category_name: product.category?.name,
          module_category_english_name: product.category?.english_name,
          module_initial_price: product.prices?.product?.original_price,
          module_discount_price: product.prices?.product?.actual_euro_price,
          module_rate: product.ratings.nb_av_rates,
          addon_name: product.technical_info.technical_name,
          module_version: product.installedVersion,
          is_subscription_plans:
            (product.prices?.plans?.length ?? 0) > 1 ? true : false,
        }
      }
    },

    allInstalledList(state: State) {
      return Object.values(state.all).filter((module) => module.installed)
    },
    
    orderConfirmationInProgress(state: State) {
      return Object.values(state.all).some(
        (module) =>
          module.orderState === ProductOrderState.checkoutModalLoading ||
          module.orderState === ProductOrderState.orderConfirmationPending,
      )
    },
  },
})

export default useProductsStore
